import { CalendarOutlined, PlusOutlined, ReloadOutlined, CopyOutlined, EyeOutlined, UnorderedListOutlined } from '@ant-design/icons';
import {
  Button,
  Flex, Radio,
  Select, Segmented, Tabs,
  Tooltip,
  notification,
} from 'antd';
import { getOrders } from 'api/orders';
import Layout from 'components/Layout';
import withGuard from 'components/withGuard';
import { AdminContext, NewOrdersContext } from 'constants/contexts';
import { PERMISSIONS } from 'constants/permissions';
import * as orderStatus from 'constants/status';
import { useHasAnyOfPermissions } from 'hooks/useHasPermission';
import React, {
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useNavigate } from 'react-router';
import { useLocation, useParams } from 'react-router-dom';
import { isOrderStatus } from 'utils/orders';
import { FILTERS, TABS as ORDERS_TAB, TYPE_OPTIONS } from './constants';
import LocationSelectInput from '../../../components/LocationSelectInput';
import SourceSelectInput from './OrderSourceSelectInput';
import PaymentTypeSelectInput from './PaymentTypeSelectInput';
import OrderOverlay from 'components/OrderOverlay';
import Table from './Table';
import OrdersCalendar from './OrdersCalendar';

function ListOrders() {
  const [search, setSearch] = useState('');
  const resetFetchRef = useRef(false);
  const [activeTab, setActiveTab] = useState('Today');
  const [previousTab, setPreviousTab] = useState('Today'); // Store previous tab for when search is cleared
  const [dataVersion, setDataVersion] = useState(1);
  const [activeFilter, setActiveFilter] = useState('In progress');
  const isAdmin = useHasAnyOfPermissions([PERMISSIONS.ADMIN_ACCESS]);
  const [filters, setFilters] = useState({
    type: '',
    parents: [],
    sources: [],
    paymentTypes: [],
  });
  const { organizationId } = useContext(AdminContext);
  const { pathname, state: locationState } = useLocation();
  const [newlyCreatedOrder, setNewlyCreatedOrder] = useState(null);
  const [viewMode, setViewMode] = useState('standard');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const navigate = useNavigate();
  const { orderId } = useParams();

  // Memoize copy handlers to prevent recreation on every render
  const createCopyHandler = useCallback((text, label) => {
    return () => {
      navigator.clipboard.writeText(text.toString()).then(() => {
        notification.success({
          message: 'Copied!',
          description: `${label} copied to clipboard`,
          duration: 2,
        });
      }).catch(() => {
        notification.error({
          message: 'Failed to copy',
          description: `Could not copy ${label} to clipboard`,
          duration: 2,
        });
      });
    };
  }, []);

  useEffect(() => {
    if (pathname !== '/console/orders' || !locationState?.newlyCreatedOrder) {
      return;
    }

    const order = locationState.newlyCreatedOrder;
    setNewlyCreatedOrder(order);
    
    // Show notification with order details
    if (locationState?.showOrderNotification) {
      const orderNumber = order?.number || order?.id || 'N/A';
      const orderId = order?.id || 'N/A';
      const notificationKey = `new-order-${orderId}`;
      
      const handleCopyOrderId = createCopyHandler(orderId, `Order ID ${orderId}`);
      const handleCopyOrderNumber = createCopyHandler(orderNumber, `Order #${orderNumber}`);
      
      const handleViewOrder = () => {
        notification.destroy(notificationKey);
        navigate(`/console/orders/${orderId}`);
      };

      notification.success({
        key: notificationKey,
        message: 'Order Created Successfully!',
        description: (
          <div style={{ marginTop: 8 }}>
            <div style={{ marginBottom: 8 }}>
              <strong>Order #:</strong> {orderNumber}
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={handleCopyOrderNumber}
                style={{ marginLeft: 8 }}
              >
                Copy
              </Button>
            </div>
            <div style={{ marginBottom: 8 }}>
              <strong>Order ID:</strong> {orderId}
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={handleCopyOrderId}
                style={{ marginLeft: 8 }}
              >
                Copy
              </Button>
            </div>
            <Button
              type="primary"
              icon={<EyeOutlined />}
              onClick={handleViewOrder}
              style={{ marginTop: 8 }}
            >
              View Order
            </Button>
          </div>
        ),
        duration: 10,
        placement: 'topRight',
      });
    }
    
    // Clear the location state to prevent showing notification on refresh
    window.history.replaceState({}, document.title);
  }, [pathname, locationState, navigate, createCopyHandler]);

  const handleRefetch = useCallback(() => setDataVersion((v) => v + 1), []);
  const handleCreateOrder = useCallback(
    () => navigate('/console/orders/direct-entry'),
    [navigate],
  );
  const handleResetFetch = useCallback(() => {
    resetFetchRef.current = true;
  }, []);
  
  const { newOrderIds, newScheduledOrderIds } = useContext(NewOrdersContext);
  
  // Memoize filtered scheduled order IDs to avoid recalculating on every render
  const filteredScheduledOrderIds = useMemo(() => {
    return newScheduledOrderIds.filter((el) => !newOrderIds.includes(el));
  }, [newOrderIds, newScheduledOrderIds]);
  
  const TABS = useMemo(() => ORDERS_TAB({
    hasNewOrder: activeTab !== 'Today' && newOrderIds.length > 0,
    hasNewScheduledOrder: filteredScheduledOrderIds.length > 0,
    isAdmin,
  }), [activeTab, newOrderIds.length, filteredScheduledOrderIds.length, isAdmin]);
  
  useEffect(() => {
    handleRefetch();
  }, [handleRefetch, newOrderIds]);

  // When search is active, bypass tab filters to search across all orders
  const isSearchActive = useMemo(() => search.trim().length > 0, [search]);
  const effectiveTab = useMemo(() => isSearchActive ? previousTab : activeTab, [isSearchActive, previousTab, activeTab]);
  const effectiveFilter = useMemo(() => isSearchActive ? 'In progress' : activeFilter, [isSearchActive, activeFilter]);

  // Memoize current tab config to avoid repeated find operations
  const currentTabConfig = useMemo(() => {
    return TABS.find((t) => t.key === effectiveTab);
  }, [TABS, effectiveTab]);

  const queryStatesIn = useMemo(() => {
    // When search is active, don't filter by state (search across all orders)
    if (isSearchActive) {
      return '';
    }
    if (effectiveTab === 'Today' && effectiveFilter === 'New') {
      return `${orderStatus.NEW}`;
    }
    // When "All orders" is selected on Today tab, don't filter by state
    if (effectiveTab === 'Today' && effectiveFilter === 'All orders') {
      return '';
    }
    return currentTabConfig?.state || '';
  }, [effectiveFilter, effectiveTab, currentTabConfig, isSearchActive]);

  // Memoize the "In progress" excluded states array
  const inProgressExcludedStates = useMemo(() => [
    orderStatus.COMPLETED,
    orderStatus.CANCELLED_BY_RESTAURANT,
    orderStatus.DELIVERED,
    orderStatus.STRANDED,
    orderStatus.PAYMENT_SCHEDULED,
    !isAdmin && orderStatus.NEEDS_APPROVAL,
  ].filter(isOrderStatus), [isAdmin]);

  const queryStatesNotIn = useMemo(() => {
    // When search is active, don't filter by state (search across all orders)
    if (isSearchActive) {
      return '';
    }
    if (effectiveTab === 'Today' && effectiveFilter === 'In progress') {
      return inProgressExcludedStates.join(',');
    }
    // When "All orders" is selected on Today tab, don't filter by state
    if (effectiveTab === 'Today' && effectiveFilter === 'All orders') {
      return '';
    }
    return currentTabConfig?.state__not_in || '';
  }, [effectiveFilter, effectiveTab, isAdmin, currentTabConfig, isSearchActive, inProgressExcludedStates]);

  const queryOrdering = useMemo(() => currentTabConfig?.order || '', [currentTabConfig]);

  const fetchOrders = useCallback(
    (pagination, _, sorter) => {
      const page = resetFetchRef.current ? 1 : pagination.current;
      
      // Handle sorting: use sorter if present, otherwise use default ordering
      // Default ordering prioritizes due time (requested_pickup_time), then new orders
      // When canceling any sort, revert to default ordering
      let ordering = queryOrdering;
      const sortKey = sorter?.columnKey || sorter?.field;
      if (sortKey && sorter?.order) {
        const direction = sorter.order === 'ascend' ? '' : '-';
        // Map frontend field names to backend field names
        const FIELD_MAP = {
          order_total: 'value', // order_total is a serializer field, backend uses 'value'
        };
        const backendField = FIELD_MAP[sortKey] || sortKey;

        // If sorting by date ascending, don't prioritize new orders (show oldest first)
        // For all other sorts, prioritize recent date first, then new orders
        // Map 'timestamp' to 'created_at' for consistency
        const sortField = sortKey === 'timestamp' ? 'created_at' : backendField;
        if (sortKey === 'timestamp' && sorter.order === 'ascend') {
          ordering = sortField; // Just date ascending, no new order priority
        } else {
          // For all other sorts, prioritize by the sort field first (most recent), then new orders
          ordering = `${direction}${sortField},-is_new`;
        }
      }
      // When sorter is empty/undefined (canceled), ordering defaults to queryOrdering
      // Default ordering is by due time (requested_pickup_time), then new orders
      
      const queries = {
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        search,
        ordering,
        state__in: queryStatesIn,
        state__not_in: queryStatesNotIn,
        order_types: filters.type,
        page,
        page_size: pagination.pageSize,
        parent__in: filters.parents.length > 0 ? filters.parents.join(',') : '',
        source__in: filters.sources.length > 0 ? filters.sources.join(',') : '',
        payment__in: filters.paymentTypes.length > 0 ? filters.paymentTypes.join(',') : '',
      };
      resetFetchRef.current = false;

      // When search is active, don't apply tab-specific filters (only_today, only_scheduled)
      // This allows searching across all orders regardless of tab
      if (!isSearchActive && currentTabConfig) {
        if (currentTabConfig.only_scheduled) {
          queries.only_scheduled = 1;
        }
        if (currentTabConfig.only_today) {
          queries.only_today = 1;
        }
      }

      return getOrders(queries)
        .then(({ data: { data: { results, count } } }) => ({
          data: results,
          total: count,
          page,
        }))
        .finally(() => {});
    },
    [
      search,
      effectiveTab,
      isSearchActive,
      dataVersion,
      queryStatesIn,
      queryStatesNotIn,
      organizationId,
      filters,
      queryOrdering,
      currentTabConfig,
    ],
  );

  const handleSearchChange = useCallback((e) => {
    const newSearchValue = e.target.value;
    const wasSearchActive = search.trim().length > 0;
    const isNowSearchActive = newSearchValue.trim().length > 0;
    
    // When search becomes active, store the current tab
    if (!wasSearchActive && isNowSearchActive) {
      setPreviousTab(activeTab);
    }
    
    handleResetFetch();
    setSearch(newSearchValue);
  }, [search, activeTab, handleResetFetch]);

  const handleChangeTab = useCallback((value) => {
    handleResetFetch();
    setActiveFilter(FILTERS[0]);
    // Only update previousTab if search is not active (when search is active, we want to keep the original tab)
    if (!isSearchActive) {
      setPreviousTab(activeTab);
    }
    setActiveTab(value);
  }, [handleResetFetch, isSearchActive, activeTab]);

  const handleChangeFilter = useCallback(({ target: { value } }) => {
    handleResetFetch();
    setActiveFilter(value);
  }, [handleResetFetch]);

  const handleChangeFilterFn = useCallback((name) => (value) => {
    setFilters((c) => ({ ...c, [name]: value }));
  }, []);

  return (
    <Layout header={(
        <Layout.Header
          title="Orders"
          onSearch={viewMode === 'standard' ? handleSearchChange : undefined}
          extra={[
            <Segmented
              key="view-switcher"
              value={viewMode}
              onChange={setViewMode}
              options={[
                {
                  value: 'standard',
                  label: (
                    <Tooltip title="Standard list view of all orders">
                      <span><UnorderedListOutlined /> Standard</span>
                    </Tooltip>
                  ),
                },
                {
                  value: 'calendar',
                  label: (
                    <Tooltip title="Calendar view of scheduled orders">
                      <span><CalendarOutlined /> Calendar</span>
                    </Tooltip>
                  ),
                },
              ]}
            />,
            <Button
              key="add-order"
              icon={<PlusOutlined />}
              onClick={handleCreateOrder}
              type="default"
            >
              Create Order
            </Button>,
            <Button
              key="refresh"
              icon={<ReloadOutlined />}
              onClick={handleRefetch}
              type="link"
            >
              Refresh
            </Button>,
          ].filter(Boolean)}
        />
      )}
      >
      <OrderOverlay
        orderId={orderId}
        onClose={() => {
          setSelectedOrder(null);
          navigate('/console/orders');
        }}
        data={selectedOrder}
        handleOrderUpdate={handleRefetch}
      />
      <div>
        {viewMode === 'standard' && (
          <>
            <Tabs items={TABS} defaultActiveKey={TABS[0].key} onChange={handleChangeTab} />
            <Flex gap={16} wrap="wrap" align="start" className="mb-4">
              <Radio.Group
                value={activeFilter}
                options={FILTERS}
                onChange={handleChangeFilter}
                optionType="button"
                disabled={activeTab !== 'Today'}
                style={{ whiteSpace: 'nowrap' }}
              />
              <Select
                value={filters.type}
                onChange={handleChangeFilterFn('type')}
                options={TYPE_OPTIONS}
              />
              <LocationSelectInput value={filters.parents} onChange={handleChangeFilterFn('parents')} />
              <SourceSelectInput value={filters.sources} onChange={handleChangeFilterFn('sources')} />
              {
                isAdmin && <PaymentTypeSelectInput value={filters.paymentTypes} onChange={handleChangeFilterFn('paymentTypes')} />
              }

            </Flex>
            <Table 
              fetchOrders={fetchOrders} 
              refetch={handleRefetch}
              newlyCreatedOrder={newlyCreatedOrder}
              onNewlyCreatedOrderHandled={() => setNewlyCreatedOrder(null)}
              onOrderSelect={setSelectedOrder}
            />
          </>
        )}
        {viewMode === 'calendar' && (
          <>
            <Flex gap={16} wrap="wrap" align="start" className="mb-4">
              <Select
                value={filters.type}
                onChange={handleChangeFilterFn('type')}
                options={TYPE_OPTIONS}
              />
              <LocationSelectInput value={filters.parents} onChange={handleChangeFilterFn('parents')} />
              <SourceSelectInput value={filters.sources} onChange={handleChangeFilterFn('sources')} />
              {isAdmin && <PaymentTypeSelectInput value={filters.paymentTypes} onChange={handleChangeFilterFn('paymentTypes')} />}
            </Flex>
            <OrdersCalendar filters={filters} dataVersion={dataVersion} />
          </>
        )}
      </div>
    </Layout>
  );
}

export default withGuard(ListOrders, [PERMISSIONS.ORDERS], { redirect: true });
